import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LABEL } from '@veriables';
import { APP_CONFIG } from '@constants';
import store from '@store';
import { getSeat, setPosition, clearCart, pushCartItems } from '@reducers/orders';
import { safeJsonParse } from '@utility/helperFunction';

export const useRowActions = ({
  seat,
  activeTab,
  menuItemsHashTable,
  menusList,
  repeat,
  refill,
  requestConfirm,
  handleManagerAuthentication,
  setConfirmPopup,
}) => {
  const navigate = useNavigate();

  const onItemAction = useCallback(
    (key, item) => {
      if (key === 'delete') {
        return requestConfirm('deleteRow', 1, item);
      }
      if (key === 'edit') {
        if (!item) return;
        const dispatch = store.dispatch;
        dispatch(getSeat(seat));
        dispatch(clearCart()).then(() => {
          const cartItems = (seat?.orders?.[0]?.items ?? [])
            .filter(e => e.pending)
            .map(it => {
              const order = safeJsonParse(it.orderJson, {});
              const modifier = order.modifierJson ? safeJsonParse(order.modifierJson, {}) : {};
              return { ...modifier, orderJson: it.orderJson, modifierJson: order.modifierJson, id: it.id, pending: true, refill: order.refill };
            });
          dispatch(pushCartItems(cartItems));
        });
        const position = seat?.seats?.filter(e => e.open).length > 0
          ? seat.seats.find(e => e.open)
          : { number: -1, position: -1 };
        dispatch(setPosition(position));
        const { menuTypeId, internalId, menuId, id } = item;
        const parsedOrderJson = safeJsonParse(item?.orderJson, {});
        const menuItemId = parsedOrderJson?.menuItemId || item.menuItemId;
        const isRefill = !!parsedOrderJson?.refill;
        navigate(
          `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}/${internalId}/1?menuId=${menuId}&checkItem=${id}&type=edit${isRefill ? '&refill=true' : ''}`,
          { replace: true }
        );
        return;
      }
      if (key === 'move') {
        const moveType = activeTab === 'pending' ? 'unsentMove' : 'sentMove';
        setConfirmPopup({ open: true, type: moveType, data: { ...seat, selectedItem: [item] } });
        return;
      }
      if (key === 'repeat') {
        const isUnsent = activeTab === 'pending';
        repeat([item], isUnsent);
        return;
      }
      if (key === 'send') {
        return requestConfirm('sendRow', 1, item);
      }
      if (key === 'void') {
        handleManagerAuthentication(
          { ...seat, selectedItem: [item] },
          LABEL().VOID_ITEM
        );
        return;
      }
      if (key === 'refill') {
        const checkItems = seat?.orders?.[0]?.items || [];
        const isItemPresent = checkItems.some(ci => ci.checkItemId === item.id);
        if (!isItemPresent) return;

        const itemSeatId = item?.seatIds?.[0] ?? item?.seats?.[0]?.seatId;
        const matchedSeat = itemSeatId != null
          ? seat?.seats?.find(s => s.id === itemSeatId)
          : null;
        // store.dispatch(setPosition(matchedSeat || { number: -1, position: -1 }));

        const { menuTypeId, menuItemId } = item;
        const menuItemData = menuItemsHashTable?.[menuItemId];
        const modifierGroupsHashTable = store.getState().orders.modifierGroupsHashTable;

        const menuCard = store.getState().orders.menuCard;
        const linkType = menuCard?.[menuTypeId]?.[menuItemId]?.menuItem?.linkType;
        // if (linkType === 1) {
        //   const menuId = menusList?.[0]?.id;
        //   navigate(
        //     `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}?menuId=${menuId}&parentMenuItemId=${menuItemId}`,
        //     { replace: true }
        //   );
        //   return;
        // }

        let requiredModCount = 0;
        const menuId = menusList?.[0]?.id;
        (menuItemData?.modifierGroupLinks || []).forEach(mg => {
          const group = modifierGroupsHashTable?.[mg.modifierGroupId];
          if (group?.minimum > 0 && group?.hiddenMenuId !== menuId) requiredModCount++;
          if (group?.flowRequired) requiredModCount++;
        });

        // Navigating into the modifier picker (OrderItemDetailV2) requires orders.seats
        // and orders.cart to be primed first, same as the 'edit' action above — otherwise
        // the item added there lands on stale/null state and the Pending Cart on /orders
        // won't reflect it until a real useGoToOrder navigation rebuilds everything.
        const primeOrderState = () => {
          const dispatch = store.dispatch;
          dispatch(getSeat(seat));
          dispatch(clearCart()).then(() => {
            const cartItems = (seat?.orders?.[0]?.items ?? [])
              .filter(e => e.pending)
              .map(it => {
                const order = safeJsonParse(it.orderJson, {});
                const modifier = order.modifierJson ? safeJsonParse(order.modifierJson, {}) : {};
                return { ...modifier, orderJson: it.orderJson, modifierJson: order.modifierJson, id: it.id, pending: true, refill: order.refill };
              });
            dispatch(pushCartItems(cartItems));
          });
          dispatch(setPosition(matchedSeat || { number: -1, position: -1 }));
        };

        if (linkType === 1) {
          primeOrderState();
          navigate(
            `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}?menuId=${menuId}&parentMenuItemId=${menuItemId}`,
            { replace: true }
          );
          return;
        }

        if (requiredModCount > 0) {
          primeOrderState();
          navigate(
            `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}?menuId=${menuId}&orderType=order&refill=true`,
            { replace: true }
          );
        } else {
          store.dispatch(setPosition(matchedSeat || { number: -1, position: -1 }));
          refill([item]);
        }
        return;
      }
    },
    [requestConfirm, navigate, activeTab, seat, repeat, refill, handleManagerAuthentication, menuItemsHashTable, menusList]
  );

  return onItemAction;
};
